import { KeyboardArrowDown } from '@mui/icons-material';
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CustomAutocomplete from 'components/shared/CustomAutocomplete/CustomAutocomplete';
import CustomDatePicker from 'components/shared/CustomDatePicker/CustomDatePicker';
import CustomTextField from 'components/shared/CustomTextField/CustomTextField';
import dayjs, { Dayjs } from 'dayjs';
import { useFormikContext } from 'formik';
import { FieldConfig, PIPELINE_MAP } from 'model/deals.model';
import { useCallback, useEffect, useState } from 'react';
import { DealsService } from 'service/deals.service';
import zeplinColor from 'theme/Color';
import {
  OPPORTUNITY_INFO_SECTION,
  SHARED_TOP_FIELDS,
  StaticDealFormFields,
} from 'utilities/constants';

const createField = (
  name: string,
  label: string,
  type: FieldConfig['type'],
  options?: readonly string[],
  autocompleteType?: 'client' | 'owner',
  xs: number = 12,
  sm: number = 6,
): FieldConfig => ({
  name,
  label,
  type,
  options,
  autocompleteType,
  xs,
  sm,
});

const createSection = (label: string): FieldConfig => ({
  name: `section.${label.toLowerCase().replace(/\s+/g, '')}`,
  label,
  type: 'section',
  xs: 12,
  sm: 12,
});

type DropdownOptions = {
  stages?: string[];
  category?: string[];
  dealStructure?: string[];
  contentRights?: string[];
  paymentTerms?: string[];
  source?: string[];
  outcome?: string[];
  lossReason?: string[];
  passReason?: string[];
  clientOrientation?: string[];
  careerStage?: string[];
  ceilingTier?: string[];
  revenueTier?: string[];
  careerStageCheck?: string[];
  clientRole?: string[];
  entertainmentCrossover?: string[];
  projectType?: string[];
};

export const ContentProduction = () => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('xl'));
  const { values, handleChange, handleBlur, setFieldValue } = useFormikContext<any>();
  const details = values.dealDetails || {};
  const pipelineType = values.pipelineType || '';

  const [clientOptions, setClientOptions] = useState<{ Name: string; Id: string }[]>([]);
  const [clientLoading, setClientLoading] = useState(false);
  const [clientTotalLength, setClientTotalLength] = useState(0);
  const [ownerOptions, setOwnerOptions] = useState<{ Name: string; Id: string }[]>([]);
  const [allOwnerOptions, setAllOwnerOptions] = useState<{ Name: string; Id: string }[]>([]);
  const [ownerLoading, setOwnerLoading] = useState(false);
  const [ownerTotalLength, setOwnerTotalLength] = useState(0);

  const [dropdownOptions, setDropdownOptions] = useState<DropdownOptions>({});

  // Convert pipelineType to API key format
  const getDealTypeKey = useCallback((pipelineType: string): string | null => {
    if (!pipelineType) return null;

    // Find the API key from PIPELINE_MAP
    const apiKey = Object.keys(PIPELINE_MAP).find((key) => PIPELINE_MAP[key] === pipelineType);

    return apiKey || null;
  }, []);

  const fetchClients = useCallback(async (searchText?: string) => {
    try {
      setClientLoading(true);
      const data = await DealsService.getAllClients(searchText);
      const clients = data?.clients || (Array.isArray(data) ? data : []);
      const formattedClients = clients
        .map((item: any) => ({
          Name: item.Name || item.name || item.clientName || '',
          Id: item.AccountId || item.Id || item.id || item.clientId || '',
        }))
        .filter((item: any) => item.Name && item.Id);
      setClientOptions(formattedClients);
      setClientTotalLength(formattedClients.length);
    } catch (error) {
      console.error('Failed to fetch clients', error);
      setClientOptions([]);
      setClientTotalLength(0);
    } finally {
      setClientLoading(false);
    }
  }, []);

  const fetchOwners = useCallback(async () => {
    try {
      setOwnerLoading(true);
      const data = await DealsService.getMembers();
      const members = data?.members || (Array.isArray(data) ? data : []);
      const formattedOwners = members
        .map((item: any) => ({
          Name: `${item.firstName} ${item.lastName}` || '',
          Id: item.email || '',
        }))
        .filter((item: any) => item.Name && item.Id);
      setAllOwnerOptions(formattedOwners);
      setOwnerOptions(formattedOwners);
      setOwnerTotalLength(formattedOwners.length);
    } catch (error) {
      console.error('Failed to fetch owners', error);
      setAllOwnerOptions([]);
      setOwnerOptions([]);
      setOwnerTotalLength(0);
    } finally {
      setOwnerLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchClients();
    fetchOwners();
  }, [fetchClients, fetchOwners]);
  // Fetch dropdowns when pipelineType changes or on mount
  useEffect(() => {
    const fetchDropdowns = async () => {
      try {
        // Use pipelineType if available, otherwise default to CONTENT_PRODUCTION
        const dealTypeKey = pipelineType
          ? getDealTypeKey(pipelineType) || 'CONTENT_PRODUCTION'
          : 'CONTENT_PRODUCTION';
        const dropdownsData = await DealsService.getDealDropdowns(dealTypeKey);

        const options: DropdownOptions = {};

        if (dropdownsData.stages && Array.isArray(dropdownsData.stages)) {
          options.stages = dropdownsData.stages.map((stage: any) => stage.stageName || stage);
        }
        if (dropdownsData.category && Array.isArray(dropdownsData.category)) {
          options.category = [...dropdownsData.category];
        }
        if (dropdownsData.dealStructure && Array.isArray(dropdownsData.dealStructure)) {
          options.dealStructure = [...dropdownsData.dealStructure];
        }
        if (dropdownsData.contentRights && Array.isArray(dropdownsData.contentRights)) {
          options.contentRights = [...dropdownsData.contentRights];
        }
        if (dropdownsData.paymentTerms && Array.isArray(dropdownsData.paymentTerms)) {
          options.paymentTerms = [...dropdownsData.paymentTerms];
        }
        if (dropdownsData.source && Array.isArray(dropdownsData.source)) {
          options.source = [...dropdownsData.source];
        }
        if (dropdownsData.outcome && Array.isArray(dropdownsData.outcome)) {
          options.outcome = [...dropdownsData.outcome];
        }
        if (dropdownsData.lossReason && Array.isArray(dropdownsData.lossReason)) {
          options.lossReason = [...dropdownsData.lossReason];
        }
        if (dropdownsData.passReason && Array.isArray(dropdownsData.passReason)) {
          options.passReason = [...dropdownsData.passReason];
        }
        if (dropdownsData.clientOrientation && Array.isArray(dropdownsData.clientOrientation)) {
          options.clientOrientation = [...dropdownsData.clientOrientation];
        }
        if (dropdownsData.revenueTier && Array.isArray(dropdownsData.revenueTier)) {
          options.revenueTier = [...dropdownsData.revenueTier];
        }
        if (dropdownsData.ceilingTier && Array.isArray(dropdownsData.ceilingTier)) {
          options.ceilingTier = [...dropdownsData.ceilingTier];
        }
        if (dropdownsData.careerStage && Array.isArray(dropdownsData.careerStage)) {
          options.careerStage = [...dropdownsData.careerStage];
        }
        if (dropdownsData.careerStageCheck && Array.isArray(dropdownsData.careerStageCheck)) {
          options.careerStageCheck = [...dropdownsData.careerStageCheck];
        }
        if (dropdownsData.clientRole && Array.isArray(dropdownsData.clientRole)) {
          options.clientRole = [...dropdownsData.clientRole];
        }
        if (
          dropdownsData.entertainmentCrossover &&
          Array.isArray(dropdownsData.entertainmentCrossover)
        ) {
          options.entertainmentCrossover = [...dropdownsData.entertainmentCrossover];
        }
        if (dropdownsData.projectType && Array.isArray(dropdownsData.projectType)) {
          options.projectType = [...dropdownsData.projectType];
        }

        setDropdownOptions(options);
      } catch (error) {
        console.error('Failed to fetch dropdown values:', error);
      }
    };

    fetchDropdowns();
  }, [pipelineType, getDealTypeKey]);

  const handleClientSearch = useCallback(
    (_: React.SyntheticEvent, value: string) => {
      fetchClients(value);
    },
    [fetchClients],
  );

  const handleOwnerSearch = useCallback(
    (_: React.SyntheticEvent, value: string) => {
      if (!value) {
        setOwnerOptions(allOwnerOptions);
        return;
      }
      const filtered = allOwnerOptions.filter((owner) =>
        owner.Name.toLowerCase().includes(value.toLowerCase()),
      );
      setOwnerOptions(filtered);
    },
    [allOwnerOptions],
  );

  const renderField = (f: FieldConfig) => {
    const raw = details[f.name.replace('dealDetails.', '')];
    const value = raw ?? (f.type === 'number' ? '' : '');

    if (f.type === 'section') {
      return (
        <Typography variant='subtitle2' sx={{ fontWeight: 600, mb: 1, mt: 2 }}>
          {f.label}
        </Typography>
      );
    }

    if (f.type === 'checkbox') {
      return (
        <FormControlLabel
          label={f.label}
          control={
            <Checkbox
              name={f.name}
              checked={Boolean(raw)}
              onChange={(e) =>
                handleChange({ target: { name: f.name, value: e.target.checked } } as any)
              }
              onBlur={handleBlur}
            />
          }
        />
      );
    }

    if (f.type === 'date') {
      let dateValue: Dayjs | null = dayjs();
      if (raw) {
        if (dayjs.isDayjs && dayjs.isDayjs(raw)) {
          dateValue = raw;
        } else if (raw instanceof Date) {
          dateValue = dayjs(raw);
        } else if (typeof raw === 'string') {
          dateValue = dayjs(raw);
        } else {
          dateValue = dayjs(raw);
        }
      }

      const dateSetter = ((newValue: Dayjs | null | ((prev: Dayjs | null) => Dayjs | null)) => {
        const actualValue = typeof newValue === 'function' ? newValue(dateValue) : newValue;
        const formattedValue = actualValue ? actualValue.toDate() : null;
        setFieldValue(f.name, formattedValue);
      }) as React.Dispatch<React.SetStateAction<Dayjs | null>>;

      return <CustomDatePicker value={dateValue} setValue={dateSetter} label={f.label} />;
    }

    if (f.type === 'autocomplete' && f.autocompleteType) {
      const fieldName = f.name.replace('dealDetails.', '');
      const currentValue = details[fieldName];

      const selectedOption =
        f.autocompleteType === 'client'
          ? clientOptions.find((opt) => opt.Name === currentValue || opt.Id === currentValue)
          : ownerOptions.find((opt) => opt.Name === currentValue || opt.Id === currentValue);

      const options = f.autocompleteType === 'client' ? clientOptions : ownerOptions;
      const loading = f.autocompleteType === 'client' ? clientLoading : ownerLoading;
      const totalLength = f.autocompleteType === 'client' ? clientTotalLength : ownerTotalLength;
      const baseHandleFilter =
        f.autocompleteType === 'client' ? handleClientSearch : handleOwnerSearch;

      const handleFilter = (event: React.SyntheticEvent, value: string) => {
        if (selectedOption && value !== selectedOption.Name) {
          setFieldValue(f.name, '');
          if (f.autocompleteType === 'client') {
            setFieldValue('dealDetails.accountId', '');
          }
        }
        baseHandleFilter(event, value);
      };

      return (
        <CustomAutocomplete
          popupIcon='/assets/Search.svg'
          required={f.required}
          options={options}
          label={f.label}
          getOptionLabel={(option: any) => option?.Name || ''}
          handleDeleteItems={() => {
            setFieldValue(f.name, '');
            if (f.autocompleteType === 'client') {
              setFieldValue('dealDetails.accountId', '');
            }
          }}
          selectedItems={{}}
          handleSelection={(option: any) => {
            setFieldValue(f.name, option?.Name || '');
            if (f.autocompleteType === 'client' && option?.Id) {
              setFieldValue('dealDetails.accountId', option.Id);
            }
          }}
          value={selectedOption || null}
          multiple={false}
          handleFilter={handleFilter}
          placeholder={f.label}
          currentLength={options.length}
          totalLength={totalLength}
          next={() => {}}
          loading={loading}
        />
      );
    }

    if (f.type === 'select' && f.options) {
      return (
        <FormControl fullWidth sx={{ width: '100%', minWidth: 0 }}>
          <InputLabel
            size={isXs ? 'small' : 'medium'}
            sx={{
              fontSize: isXs ? 14 : 16,
              '& .MuiFormLabel-asterisk': {
                color: zeplinColor.Error,
              },
            }}
          >
            {f.label}
          </InputLabel>
          <Select
            IconComponent={KeyboardArrowDown}
            name={f.name}
            required={f.name === 'stageName'}
            value={value ?? ''}
            label={f.label}
            onChange={handleChange}
            onBlur={handleBlur}
            size={isXs ? 'small' : 'medium'}
            sx={{
              width: '100%',
              minWidth: 0,
              '& .MuiSelect-select': {
                fontSize: isXs ? 14 : 16,
              },
            }}
          >
            {f.options.map((opt) => (
              <MenuItem key={String(opt)} value={opt}>
                {opt}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    return (
      <CustomTextField
        style={{ width: '100%' }}
        name={f.name}
        label={f.label}
        type={f.type === 'number' ? 'number' : f.type === 'textarea' ? 'text' : 'text'}
        text={value ?? ''}
        onChange={handleChange}
        onBlur={() => handleBlur(f.name as any)}
        multiline={f.multiline}
        form={true}
      />
    );
  };

  const opportunityInfoFields: FieldConfig[] = [
    createField(
      'dealDetails.revenueTier',
      'Revenue tier',
      'select',
      dropdownOptions.revenueTier as readonly string[],
    ),
    createField(
      'dealDetails.ceilingTier',
      'Ceiling tier',
      'select',
      dropdownOptions.ceilingTier as readonly string[],
    ),
    createField(
      'dealDetails.clientOrientation',
      'Client orientation',
      'select',
      dropdownOptions.clientOrientation as readonly string[],
    ),
    createField(
      'dealDetails.careerStage',
      'Career stage',
      'select',
      dropdownOptions.careerStage as readonly string[],
    ),
  ];

  const dualRevenueFields: FieldConfig[] = [
    createField('dealDetails.clientTalentFee', 'Client talent fee ($)', 'number'),
    createField('dealDetails.talentFeeCommissionRate', 'Talent fee commission rate (%)', 'number'),
    createField('dealDetails.talentFeeCommission', 'Talent fee commission', 'number'),
    createField('dealDetails.3ASProductionFee', '3AS production fee ($)', 'number'),
    createField('dealDetails.total3ASRevenue', 'Total 3AS revenue', 'number'),
    createField('dealDetails.ownedRevenue', 'Owned revenue (%)', 'number'),
  ];

  const financialInfoFields: FieldConfig[] = [
    createField('dealDetails.dealValue', 'Deal value (gross)', 'text'),
    createField('dealDetails.commissionRate', 'Commission rate (%)', 'number'),
    createField('dealDetails.commissionValue', 'Commission value', 'number'),
    createField(
      'dealDetails.dealStructure',
      'Deal structure',
      'select',
      dropdownOptions.dealStructure as readonly string[],
    ),
    createField('dealDetails.equity', 'Equity/ownership (%)', 'number'),
    createField('dealDetails.backend', 'Backend (%)', 'text'),
    createField(
      'dealDetails.closeDate',
      'Expected close date',
      'date',
      undefined,
      undefined,
      12,
      12,
    ),
  ];

  const projectInfoFields: FieldConfig[] = [
    createField(
      'dealDetails.projectType',
      'Project type',
      'select',
      dropdownOptions.projectType as readonly string[],
    ),
    createField(
      'dealDetails.entertainmentCrossover',
      'Entertainment crossover',
      'select',
      dropdownOptions.entertainmentCrossover as readonly string[],
    ),
    createField(
      'dealDetails.entertainmentCrossover',
      'Career Stage Check',
      'select',
      dropdownOptions.careerStageCheck as readonly string[],
    ),
    createField('dealDetails.lg3AMsInvolved', 'LG/3AMs involved', 'checkbox'),
    createField(
      'dealDetails.clientRole',
      'Client role',
      'select',
      dropdownOptions.clientRole as readonly string[],
    ),
    createField('dealDetails.clientOwnership', 'Client ownership (%)', 'number'),
    createField('dealDetails.3ASOwnership', '3AS ownership (%)', 'number'),
    createField('dealDetails.buyerPlatform', 'Buyer/Platform', 'autocomplete', undefined, 'client'),
  ];

  const updatedStaticFields = StaticDealFormFields.map((field) => {
    if (field.name === 'dealDetails.source' && dropdownOptions.source) {
      return { ...field, options: dropdownOptions.source as readonly string[] };
    }
    if (field.name === 'dealDetails.outcome' && dropdownOptions.outcome) {
      return { ...field, options: dropdownOptions.outcome as readonly string[] };
    }
    if (field.name === 'dealDetails.lossReason' && dropdownOptions.lossReason) {
      return { ...field, options: dropdownOptions.lossReason as readonly string[] };
    }
    if (field.name === 'dealDetails.passReason' && dropdownOptions.passReason) {
      return { ...field, options: dropdownOptions.passReason as readonly string[] };
    }
    return field;
  });

  const attributionFields = updatedStaticFields.filter(
    (field) =>
      field.name.startsWith('dealDetails.leadershipSponsor') ||
      field.name.startsWith('dealDetails.source') ||
      field.name.startsWith('dealDetails.partnerAccount') ||
      field.name.startsWith('dealDetails.partnerContact') ||
      field.name.startsWith('dealDetails.daysInCurrentStage'),
  );

  const additionalInfoFields = updatedStaticFields.filter(
    (field) =>
      field.name.startsWith('dealDetails.outcome') ||
      field.name.startsWith('dealDetails.lossReason') ||
      field.name.startsWith('dealDetails.passReason') ||
      field.name.startsWith('dealDetails.competitor'),
  );

  const notesFields = updatedStaticFields.filter((field) =>
    field.name.startsWith('dealDetails.notes'),
  );

  const allFields: FieldConfig[] = [
    OPPORTUNITY_INFO_SECTION,
    ...SHARED_TOP_FIELDS,
    createField(
      'dealDetails.stageName',
      'Stage',
      'select',
      dropdownOptions.stages as readonly string[],
    ),
    ...opportunityInfoFields,
    createSection('Financial Information (Dual revenue)'),
    ...dualRevenueFields,
    createSection('Financial Information'),
    ...financialInfoFields,
    createSection('Project Information'),
    ...projectInfoFields,
    createSection('Attribution & Tracking'),
    ...attributionFields,
    createSection('Additional Information'),
    ...additionalInfoFields,
    createSection('Notes'),
    ...notesFields,
  ];

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 2, width: '100%' }}>
      {allFields.map((f) => (
        <Box key={f.name} sx={{ gridColumn: { xs: 'span 12', sm: `span ${f.sm ?? f.xs ?? 12}` } }}>
          {renderField(f)}
        </Box>
      ))}
    </Box>
  );
};
